import { FormField } from '@/components/ui/form';
import { useFormContext, useFormState } from 'react-hook-form';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { DiscountCodeZ as DiscountCode } from '@libs/schemas';
import { DatePicker } from 'src/components/DatePicker';

const UsageLimitField = () => {
  const form = useFormContext<DiscountCode>();
  const { errors } = useFormState({ control: form.control });
  return (
    <FormField
      control={form.control}
      name="usageLimit"
      render={({ field }) => (
        <div>
          <Label htmlFor="usageLimit">Usage Limit</Label>
          <Input
            id="usageLimit"
            type="number"
            min="1"
            value={field.value?.toString() ?? ''}
            onChange={(e) => field.onChange(e.target.value === '' ? null : Number(e.target.value))}
            onBlur={field.onBlur}
            placeholder="Leave empty for unlimited"
          />
          {errors.usageLimit && (
            <p className="text-sm text-red-500 mt-1 dark:text-red-900">
              {errors.usageLimit.message}
            </p>
          )}
          <p className="text-sm text-slate-500 mt-1 dark:text-slate-400">
            Maximum number of times this code can be used. Leave empty for unlimited usage.
          </p>
        </div>
      )}
    />
  );
};

const ExpirationDateField = () => {
  const form = useFormContext<DiscountCode>();
  const { errors } = useFormState({ control: form.control });
  return (
    <FormField
      control={form.control}
      name="expiresAt"
      render={({ field }) => (
        <div>
          <Label htmlFor="expiresAt">Expiration Date</Label>
          <DatePicker
            date={field.value ? new Date(field.value) : new Date()}
            setDate={(date) => field.onChange(date ? date.toISOString() : undefined)}
            fromDate={new Date()}
            allowClear={false}
          />
          <p className="text-sm text-slate-500 mt-1 dark:text-slate-400">
            Date after which this code will no longer be valid
          </p>
          {errors.expiresAt && (
            <p className="text-sm text-red-500 mt-1 dark:text-red-900">
              {errors.expiresAt.message}
            </p>
          )}
        </div>
      )}
    />
  );
};

const ActiveField = () => {
  const form = useFormContext<DiscountCode>();
  return (
    <FormField
      control={form.control}
      name="active"
      render={({ field }) => (
        <div className="flex items-center space-x-2">
          <Switch
            id="active"
            checked={field.value}
            onCheckedChange={field.onChange}
            className="data-[state=checked]:bg-primary"
          />
          <Label htmlFor="active">Active</Label>
          <p className="text-sm text-slate-500 ml-2 dark:text-slate-400">
            Code can be used immediately when active
          </p>
        </div>
      )}
    />
  );
};

export const UsageSettingsFormField = () => {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Usage Settings</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <UsageLimitField />
        <ExpirationDateField />
        <ActiveField />
      </CardContent>
    </Card>
  );
};
